import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Typography,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

// Interface para expor métodos via ref
export interface OptionsMenuRef {
  openLogoutModal: () => void;
}

interface OptionsMenuProps {
  toggleColorMode: () => void; // Prop para alterar o tema
}

export const useLogout = () => {
  const navigate = useNavigate();
  return () => {
    sessionStorage.clear();
    navigate("/login"); // Redireciona para login
  };
};

const OptionsMenu = forwardRef<OptionsMenuRef, OptionsMenuProps>(
  ({ toggleColorMode }, ref) => {
    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
    const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
    const [aboutText, setAboutText] = useState("");
    const [version, setVersion] = useState("");
    const [build, setBuild] = useState(""); // novo
    const theme = useTheme();
    const handleLogout = useLogout();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isClicking, setIsClicking] = useState(false); // Flag para controlar o clique
    const [isModalOpen, setIsModalOpen] = useState(false); // Flag para verificar se o modal está aberto

    // Métodos para abrir e fechar modais
    const openLogoutModal = () => setIsLogoutModalOpen(true);
    const closeLogoutModal = () => setIsLogoutModalOpen(false);
    const openAboutModal = () => {
      setIsModalOpen(true); // Marca o modal como aberto
      setIsAboutModalOpen(true);
    };
    const closeAboutModal = () => {
      setIsModalOpen(false); // Marca o modal como fechado
      setIsAboutModalOpen(false);
    };

    // Expor métodos via ref
    useImperativeHandle(ref, () => ({
      openLogoutModal,
    }));

    const handleMenuOpen = () => {
      if (isModalOpen || isLogoutModalOpen) return; // Bloqueia reabertura se qualquer modal estiver aberto
      setIsMenuOpen(true);
    };
    

    const handleMenuClose = () => {
      setIsMenuOpen(false);
    };

    // Carrega o texto e a versão do arquivo "sobre.txt"
    useEffect(() => {
      fetch("/sobre.txt")
        .then((response) => response.text())
        .then((text) => {
          const lines = text.split("\n");
    
          const versionLine = lines.find((line) => line.startsWith("VERSAO:"));
          const buildLine = lines.find((line) => line.startsWith("BUILD:"));
    
          if (versionLine) {
            setVersion(versionLine.replace("VERSAO:", "").trim());
          }
    
          if (buildLine) {
            setBuild(buildLine.replace("BUILD:", "").trim());
          }
    
          // Remove as linhas de VERSAO e BUILD do texto
          setAboutText(
            lines
              .filter(
                (line) => !line.startsWith("VERSAO:") && !line.startsWith("BUILD:")
              )
              .join("\n")
          );
        })
        .catch((error) =>
          console.error("Erro ao carregar o texto do arquivo:", error)
        );
    }, []);
    

    return (
      <Box
        onMouseEnter={handleMenuOpen} // Abre o menu ao passar o mouse sobre a área
        onMouseLeave={handleMenuClose} // Fecha o menu quando o mouse sai da área
        sx={{
          position: "relative",
          display: "inline-block",
          zIndex: 300, // Aumenta o zIndex para garantir que o menu fique acima de outros elementos
        }}
      >
        {/* Texto Opções */}
        <Typography
          sx={{
            color: theme.palette.text.primary,
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          Opções
        </Typography>

        {/* Menu dentro da mesma box */}
        {isMenuOpen && (
          <Box
            sx={{
              position: "absolute",
              top: "100%", // Alinha o menu logo abaixo do texto
              left: 0,
              zIndex: 250, // Aumenta o zIndex para garantir que o menu fique acima de outros componentes
              backgroundColor:
                theme.palette.mode === "dark" ? "#424242" : "#d4eaff",
              color: theme.palette.text.primary,
              borderColor:
                theme.palette.mode === "dark" ? "#616161" : "#b0bec5",
              borderRadius: 1,
              boxShadow: "0px 6px 10px rgba(0,0,0,0.5)",
              minWidth: "90px",
              overflow: "visible", // Garantir que o conteúdo do menu não seja cortado
            }}
          >
            <MenuItem
              onClick={() => {
                setIsClicking(true); // Marca o clique para evitar reabertura
                handleMenuClose(); // Fecha o menu imediatamente

                // Um pequeno delay antes de abrir o modal para evitar a reabertura do menu
                setTimeout(() => {
                  openAboutModal(); // Abre o modal de "Sobre"
                  setIsClicking(false); // Restaura o estado após a ação
                });
              }}
              sx={{
                color: theme.palette.text.primary,
              }}
            >
              Sobre
            </MenuItem>

            <MenuItem
              onClick={() => {
                handleMenuClose(); // Fecha o menu
                toggleColorMode(); // Altera o tema
              }}
              sx={{
                color: theme.palette.text.primary,
              }}
            >
              Alterar tema
            </MenuItem>

            <MenuItem
  onClick={() => {
    setIsClicking(true); // Marca o estado de clique
    openLogoutModal(); // Abre o modal de logout
    handleMenuClose(); // Fecha o menu

    // Restaura o estado de clique após um pequeno delay
    setTimeout(() => setIsClicking(false), 300);
  }}
  sx={{
    color: theme.palette.text.primary,
  }}
>
  Sair (Logout)
</MenuItem>

          </Box>
        )}

        {/* Modal de Logout */}
        <Dialog
          open={isLogoutModalOpen}
          onClose={closeLogoutModal}
          aria-labelledby="logout-confirmation-dialog"
        >
          <DialogTitle id="logout-confirmation-dialog">Logout</DialogTitle>
          <DialogContent>
            <Typography>Tem certeza de que deseja sair?</Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center" }}>
            <Button onClick={closeLogoutModal} color="primary">
              Cancelar
            </Button>
            <Button onClick={handleLogout} color="error" variant="contained">
              Sair
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal Sobre */}
        <Dialog
          open={isAboutModalOpen}
          onClose={closeAboutModal}
          aria-labelledby="about-dialog"
        >
          <DialogTitle
            id="about-dialog"
            sx={{
              textAlign: "center",
              fontWeight: "bold",
            }}
          >
            Sobre o GDU
          </DialogTitle>
          <DialogContent>
            {aboutText.split("\n\n").map((block, index) => (
              <Typography
                key={index}
                sx={{
                  textAlign: "center",
                  marginBottom:
                    index === aboutText.split("\n\n").length - 1 ? "0" : "20px",
                }}
              >
                {block
                  .trim()
                  .split("\n")
                  .map((line, lineIndex) => (
                    <span key={lineIndex}>
                      {line}
                      {lineIndex !== block.trim().split("\n").length - 1 && (
                        <br />
                      )}
                    </span>
                  ))}
              </Typography>
            ))}
          </DialogContent>
          <DialogActions
  sx={{
    justifyContent: "center",
    position: "relative",
  }}
>
  <Button
    onClick={closeAboutModal}
    color="primary"
    variant="contained"
  >
    OK
  </Button>

  {/* Versão e Build alinhados à direita */}
  <Box
    sx={{
      position: "absolute",
      right: "16px",
      top: "50%",
      transform: "translateY(-50%)",
      textAlign: "right",
      lineHeight: 1.2,
    }}
  >
    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
      {version}
    </Typography>
    <Typography variant="caption">
      {build}
    </Typography>
  </Box>
</DialogActions>

        </Dialog>
      </Box>
    );
  }
);

export default OptionsMenu;
